import { useState, useCallback, useRef, useEffect } from "react";
import { Sketch } from "@uiw/react-color";

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

export default function ColorPicker({ color, onChange }: ColorPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Usar cor padrão se estiver vazia
  const displayColor = color || "#000000";

  // Função para verificar se a cor tem transparência
  const hasTransparency = (colorStr: string): boolean => {
    if (colorStr.startsWith("rgba(")) {
      const match = colorStr.match(/rgba\([^)]+,\s*([^)]+)\)/);
      if (match) {
        const alpha = parseFloat(match[1]);
        return alpha < 1;
      }
    }
    return false;
  };

  // Fechar o picker quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker]);

  const handleColorChange = useCallback(
    (color: any) => {
      if (color.rgba && color.rgba.a < 1) {
        const { r, g, b, a } = color.rgba;
        onChange(
          `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`
        );
      } else {
        onChange(color.hex || color.hexa || displayColor);
      }
    },
    [onChange, displayColor]
  );

  return (
    <div className="relative w-full">
      <div
        ref={buttonRef}
        className="w-full h-10 rounded-md border border-gray-200 cursor-pointer flex items-center justify-center relative overflow-hidden transition-all duration-200 hover:border-gray-300 shadow-sm hover:shadow-md"
        style={{ backgroundColor: displayColor }}
        onClick={() => {
          if (!showPicker && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const pickerWidth = 280;
            const pickerHeight = 320; // altura aproximada do color picker
            
            // Posicionar a 60% da altura da tela
            const top = Math.max(20, viewportHeight * 0.7 - pickerHeight / 2);
            
            // Posicionar horizontalmente alinhado com o sidebar/componente
            let left = rect.left;
            // Se estiver muito à direita, alinhar pela direita do componente
            if (left + pickerWidth > viewportWidth - 20) {
              left = rect.right - pickerWidth;
            }
            // Garantir margem mínima da esquerda
            left = Math.max(20, left);
            
            setPickerPosition({
              top: top,
              left: left
            });
          }
          setShowPicker(!showPicker);
        }}
      >
        {hasTransparency(displayColor) && (
          <div className="absolute inset-0 rounded-md bg-checkerboard opacity-20" />
        )}
      </div>

        {showPicker && (
          <div
            ref={pickerRef}
            className="fixed z-[9999] glass-modal rounded-xl color-picker-slide"
            style={{
              top: `${pickerPosition.top}px`,
              left: `${pickerPosition.left}px`,
            }}
          >
            <Sketch
              color={displayColor}
              onChange={handleColorChange}
              style={{ boxShadow: "none" }}
              presetColors={[]} // Remove a paleta de cores padrão
            />
          </div>
        )}
    </div>
  );
}
